import { ToastContainer } from 'react-toastify';
import Footer from './components/Footer';
import { ModalProvider } from './components/PreLaunch/ModalContext';
import Navbar from './components/Navbar';
import Modal from './components/PreLaunch/Modal';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import Modals from './views/Modals';
import Gifts from './views/Gifts';
import AppLayout from './layouts/AppLayout';
import CreateGiftFlow from './views/Gifts/CreateGiftFlow';
import GiftsList from './views/Gifts/GiftsList';
import GiftDetails from './views/Gifts/Gift/index.jsx';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import { useEffect, useRef, useState } from 'react';
import { UPDATE_BALANCE } from './common/constants.js';
import { useSolana } from './hooks/useSolana.js';
import { closeModal, openModal } from './redux/slices/modals.js';
import { useDispatch, useSelector } from 'react-redux';
import GiftClaimRouter from './views/Gifts/Gift/GiftClaimRouter';
import Status from './views/Gifts/Gift/Status';
import styled, { useTheme } from 'styled-components';
import { selectSolanaUSDCBalance } from './redux/slices/walletSlice';
import { selectThemeMode } from './redux/slices/themeSlice';
import Dashboard from './views/Dashboard/index.jsx';
import CheckEmail from './views/Gifts/Gift/CheckEmail.jsx';
import SurveyPromo from './components/PreLaunch/SurveyPromo';
import ClaimPromoCodeGift from './views/Gifts/Gift/ClaimPromoCodeGift';
import { isAccessingFromEurope } from './common/region.js';
import {
  selectPrivyToken,
  setIsEurope,
  setPrivyToken,
} from './redux/slices/auth.js';
import Partners from './components/Partners/index';
import Referrals from './components/Referrals/index';
import BridgeCallback from './components/BridgeCallback';
import BridgeKycCallback from './components/BridgeKycCallback';
import TokenSubmission from './views/TokenSubmission/index.jsx';
import TokenSubmissionResponse from './views/TokenSubmissionResponse/index.jsx';
import RedirectComponent from './components/RedirectComponent/index.jsx';
import GiftRedirect from './components/GiftRedirect/index.jsx';
import { usePrivy, useUser } from '@privy-io/react-auth';
import TokenHome from './views/TokenHome/index.jsx';
import {
  useLazyGetCurrentUserProfileQuery,
  useSetFirebaseTokenMutation,
} from './redux/api/authorized/auth.js';
import Home from './views/Home/index.jsx';
import MobileNavigation from './components/MobileNavigation/index.jsx';
import Pricing from './views/Pricing/index.jsx';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import Admin from './views/Admin/index.jsx';
import Trade from './views/Trade/index.jsx';
import { killPreloader } from './common/loading.js';
import RewardsView from './views/Rewards/index.jsx';
import LandingV2 from './views/LandingV2/index.jsx';
import {
  initPush,
  setupPushNotificationClick,
  checkInitialNotification,
} from './common/firebase.js';
import MobileLogin from './views/Mobile/Login/index.jsx';
import { useSystemTheme } from './hooks/useSystemTheme.js';
import Swap from './components/Swap';
import '@ionic/react/css/core.css';
import theme from './common/theme.js';
import { ThemeProvider } from 'styled-components';
import {
  IonApp,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonMenu,
  IonTitle,
  IonToolbar,
  setupIonicReact,
} from '@ionic/react';
import DropdownContent from './components/NavUserProfile/DropdownContent.jsx';
import { useCreateMockUserMutation } from './redux/api/authorized/privy';
import { closeOutline } from 'ionicons/icons';
import { NotificationsContent } from './components/NavUserProfile/Notifications/index.jsx';
import { menuController } from '@ionic/core/components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from './common/responsive.js';
import WalletProvider from './components/WalletProvider';
import DownloadApp from './views/DownloadApp/index.jsx';
import { hapticsImpactLight } from './common/haptic.js';
import { useFlag, useFlags, useFlagsStatus } from '@unleash/proxy-client-react';
import Physical from './views/Physical/index.jsx';
import Cards from './views/Cards/index.jsx';
import UpdateRequired from './views/UpdateRequired/index.jsx';
import SpendGallery from './views/Spend/SpendGallery/index.jsx';
import SpendGiftCardDetail from './views/Spend/SpendGiftCardDetail/index.jsx';
import SpendCheckout from './views/Spend/SpendCheckout/index.jsx';
import MyGiftCards from './views/Spend/MyGiftCards/index.jsx';
import SpendCategory from './views/Spend/SpendCategory/index.jsx';
import { useCheckAppUpdateMutation } from './redux/api/public/app.js';
import NativeSelect from './components/NativeSelect/index.jsx';
import DiscountCodeRoute from './components/DiscountCodeRoute/index.jsx';
import Checkout from './views/Checkout/index.jsx';
import StripeCheckout from './views/StripeCheckout/index.jsx';
import Buy from './views/Buy/index.jsx';
import { CHECKOUT_RETURN_STORAGE_KEY } from './common/checkoutTokens';
import { useCheckUnauthenticatedSessionMutation } from './redux/api/public';
import {
  setValidationResult,
  selectValidationType,
} from './redux/slices/sessionValidation';
import { setFeatureFlags } from './common/analytics.js';

setupIonicReact();

// Configure Capacitor Keyboard for native resize mode
// This ensures the screen properly resizes when keyboard appears
// and enables scrolling so inputs can scroll into view
if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Keyboard')) {
  Keyboard.setResizeMode({ mode: 'native' });
  Keyboard.setScroll({ isDisabled: false });
}

const LandingContainer = styled.div`
  img {
    max-width: 100%;
  }
`;

/** Route that opens the Add Funds / Deposit modal (or Privy login if not authenticated) */
function OpenDepositModalRoute() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authenticated, ready, login: privyLogin } = usePrivy();

  useEffect(() => {
    if (!ready) return;
    if (!authenticated) {
      privyLogin();
      return;
    }
    dispatch(openModal({ name: 'addFunds' }));
    navigate('/app/home', { replace: true });
  }, [ready, authenticated, privyLogin, dispatch, navigate]);

  return null;
}

function ReactApp() {
  const { refreshTokenBalances, embeddedWallet } = useSolana();
  const dispatch = useDispatch();
  const usdcBalance = useSelector(selectSolanaUSDCBalance);
  const { authenticated, ready, getAccessToken } = usePrivy();
  const { user: privyUser, refreshUser } = useUser();
  const location = useLocation();
  const privyToken = useSelector(selectPrivyToken);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserRetries, setCurrentUserRetries] = useState(0);
  const [pendingRoute, setPendingRoute] = useState(null);

  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');

  const [getCurrentUserProfile] = useLazyGetCurrentUserProfileQuery();

  const [setFirebaseToken] = useSetFirebaseTokenMutation();

  const [checkUnauthenticatedSession] =
    useCheckUnauthenticatedSessionMutation();

  const validationType = useSelector(selectValidationType);

  const navigate = useNavigate();

  useEffect(() => {
    if (lpd && authenticated && ready) {
      navigate('/app/home', { replace: true });
    }
  }, [lpd, authenticated, ready, navigate]);

  useEffect(() => {
    const setToken = async () => {
      const token = await getAccessToken();
      dispatch(setPrivyToken(token));
    };

    if (authenticated && !privyToken) {
      setToken();
    }
  }, [authenticated, getAccessToken, privyToken, dispatch]);

  useEffect(() => {
    let loop;
    if (authenticated) {
      loop = setInterval(async () => {
        const newToken = await getAccessToken();
        if (newToken !== privyToken) {
          dispatch(setPrivyToken(newToken));
        }
      }, 1000);
    }
    return () => {
      if (loop) {
        clearInterval(loop);
      }
    };
  }, [authenticated, getAccessToken, privyToken]);

  const [createMockUser] = useCreateMockUserMutation();
  const [checkAppUpdate] = useCheckAppUpdateMutation();

  useEffect(() => {
    const getCurrentUser = async () => {
      if (privyUser && privyToken && !currentUser) {
        if (currentUserRetries > 5) {
          return;
        }
        const result = await getCurrentUserProfile();
        const resp = result.isError ? null : result.data;

        if (!resp) {
          if (import.meta.env.DEV) {
            createMockUser({ privyId: privyUser.id });
          }
          await new Promise((resolve) =>
            setTimeout(resolve, (currentUserRetries + 1) * 200),
          );
          setCurrentUserRetries(currentUserRetries + 1);
        } else {
          setCurrentUser(resp);
          if (!embeddedWallet) {
            refreshUser();
          }
        }
      }
    };

    getCurrentUser();
  }, [
    privyUser,
    privyToken,
    currentUserRetries,
    createMockUser,
    currentUser,
    embeddedWallet,
  ]);

  // Check unauthenticated session when ready and not authenticated
  useEffect(() => {
    const checkUnauthenticated = async () => {
      // Only check if:
      // 1. Privy is ready
      // 2. User is NOT authenticated
      // 3. We haven't checked for unauthenticated yet (check once per session)
      if (ready && !authenticated && validationType !== 'unauthenticated') {
        try {
          const result = await checkUnauthenticatedSession().unwrap();
          dispatch(
            setValidationResult({
              result,
              type: 'unauthenticated',
            }),
          );
        } catch (error) {
          // Silently fail - don't block the app
          console.warn('Failed to check unauthenticated session:', error);
        }
      }
    };

    checkUnauthenticated();
  }, [
    ready,
    authenticated,
    validationType,
    checkUnauthenticatedSession,
    dispatch,
  ]);

  useSystemTheme();

  // After Apple OAuth from checkout, redirect back to checkout with params so Apple Pay can auto-open
  useEffect(() => {
    if (!ready || !authenticated) return;
    try {
      const raw = sessionStorage.getItem(CHECKOUT_RETURN_STORAGE_KEY);
      if (!raw) return;
      const { path, amount } = JSON.parse(raw);
      if (path && typeof amount === 'number') {
        sessionStorage.removeItem(CHECKOUT_RETURN_STORAGE_KEY);
        navigate(`${path}?fromAppleLogin=1&amount=${amount}`, {
          replace: true,
        });
      }
    } catch (_) {
      try {
        sessionStorage.removeItem(CHECKOUT_RETURN_STORAGE_KEY);
      } catch (_) {}
    }
  }, [ready, authenticated, navigate]);

  useEffect(() => {
    App.addListener('appUrlOpen', (event) => {
      // Support deep links and universal links
      const slug = event.url
        .replace('https://deks.xyz', '')
        .replace('http://deks.xyz', '')
        .replace('deks:/', '');
      if (slug) {
        window.location.replace(slug);
      }
    });

    // Listen for app state changes to check for notifications when app comes to foreground
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('App')) {
      App.addListener('appStateChange', async (state) => {
        if (state.isActive) {
          // When app comes to foreground, check for initial notification
          // This handles cases where notificationActionPerformed doesn't fire
          if (Capacitor.isPluginAvailable('FirebaseMessaging')) {
            try {
              checkInitialNotification((route) => {
                setPendingRoute(route);
              });
            } catch (error) {
              // Silently fail - notification check is best effort
            }
          }
        }
      });
    }
  }, []);

  // Register push notification *click* listener as soon as app loads (no login required).
  // If user taps notification while logged out, we store the route and redirect after login.
  const authRef = useRef(authenticated);
  authRef.current = authenticated;
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  useEffect(() => {
    if (
      !Capacitor.isNativePlatform() ||
      !Capacitor.isPluginAvailable('FirebaseMessaging') ||
      !navigate
    ) {
      return;
    }
    let handles;
    let cancelled = false;
    setupPushNotificationClick((route) => {
      try {
        setPendingRoute(route);
      } catch (e) {
        console.error('Push notification route handling failed', e);
      }
    })
      .then((h) => {
        if (cancelled) {
          h?.remove?.();
        } else {
          handles = h;
        }
      })
      .catch((error) => {
        console.error('Error setting up push notification listener:', error);
      });
    return () => {
      cancelled = true;
      handles?.remove?.();
    };
  }, [navigate]); // Removed currentUser from dependencies to prevent re-registration

  // After login, redirect to route from push notification if one was stored
  useEffect(() => {
    if (!authenticated || !currentUser || !navigate || !pendingRoute) {
      return;
    }

    const routeToNavigate = pendingRoute;
    setPendingRoute(null);
    navigate(routeToNavigate);
  }, [authenticated, currentUser, navigate, pendingRoute]);

  useEffect(() => {
    if (
      privyToken &&
      setFirebaseToken &&
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable('FirebaseMessaging')
    ) {
      initPush((token) => {
        setFirebaseToken(token);
      });
    }
  }, [setFirebaseToken, privyToken]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const referralCode = searchParams.get('rc');
    if (referralCode) {
      localStorage.setItem('rc', referralCode);
    }
  }, [location.search]);

  useEffect(() => {
    (async () => {
      const isEurope = await isAccessingFromEurope();
      dispatch(setIsEurope(isEurope));
    })();
  }, []);

  useEffect(() => {
    const listener = window.addEventListener('message', async (e) => {
      if (e?.data?.type === UPDATE_BALANCE) {
        await refreshTokenBalances();
        dispatch(closeModal());
      }
      // Detect-deposit modal: not used for now. Previously opened when pay.coinbase.com
      // posted "success" (e.g. after Add Funds via Coinbase); we've disabled it and
      // removed the sessionStorage/onramp_api logic. Uncomment to re-enable.
      // if (
      //   e?.data &&
      //   e?.data?.includes &&
      //   e?.data?.includes('success') &&
      //   e?.origin === 'https://pay.coinbase.com'
      // ) {
      //   dispatch(
      //     openModal({
      //       name: 'detectDeposit',
      //       props: { initialBalance: usdcBalance },
      //     }),
      //   );
      // }
    });
    return removeEventListener('message', listener);
  }, [usdcBalance]);

  useEffect(() => {
    if (ready) {
      const searchParams = new URLSearchParams(location.search);
      const giftCode = searchParams.get('gift');
      if (giftCode) {
        // If there is a gift code, wait for the redirect to happen.
        return;
      }
      if (authenticated && currentUser) {
        killPreloader();
      } else if (!authenticated) {
        killPreloader();
      }
    }
  }, [authenticated, ready, currentUser, location.search]);

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const Container = isMobile ? IonApp : 'div';

  const currentTheme = useTheme();
  const themeMode = useSelector(selectThemeMode);
  const { flagsReady } = useFlagsStatus();
  const flags = useFlags();
  const appRequiredForClaim = useFlag('appRequiredForClaim');
  const enableSpend = useFlag('enableSpend');
  const isApp = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!flagsReady) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const modalName = searchParams.get('modal');
    const giftCode = searchParams.get('gift');
    // Handle modal query parameter
    if (authenticated && privyToken && modalName) {
      dispatch(openModal({ name: modalName }));
    }

    // Handle gift redirect logic
    if (giftCode) {
      // If the app is required for claim and the user is on mobile and not on the app, show the download page
      const shouldShowDownload = appRequiredForClaim && isMobile && !isApp;
      navigate(
        shouldShowDownload ? '/download' : `/app/gifts/${giftCode}/promo-claim`,
        {
          replace: true,
        },
      );
    }
  }, [
    authenticated,
    privyToken,
    location.search,
    dispatch,
    navigate,
    appRequiredForClaim,
    flagsReady,
  ]);

  useEffect(() => {
    if (Capacitor.isNativePlatform() && ready && !authenticated) {
      if (
        location.pathname !== '/' &&
        location.pathname !== '/download' &&
        !location.pathname.includes('/app/gifts/')
      ) {
        navigate('/');
      }
    }
  }, [authenticated, ready]);

  useEffect(() => {
    if (flagsReady) {
      let flagsObject = {};
      for (const flag of flags) {
        flagsObject[flag.name] = String(flag.enabled);
      }
      setFeatureFlags(flagsObject);
    }
  }, [flags, flagsReady]);

  useEffect(() => {
    const checkForUpdates = async () => {
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('App')) {
        try {
          const appInfo = await App.getInfo();
          const platform = Capacitor.getPlatform();
          const result = await checkAppUpdate({
            os: platform,
            appVersion: appInfo?.version,
          }).unwrap();

          if (result.requiresUpdate) {
            navigate('/update-required', { replace: true });
          }
        } catch (error) {
          console.error('Error checking for app updates:', error);
        }
      }
    };

    checkForUpdates();
  }, [checkAppUpdate, navigate]);

  return (
    <Container
      style={{
        backgroundColor: currentTheme.background.primary,
        color: currentTheme.text.regular,
      }}
    >
      <ModalProvider>
        <Routes>
          <Route path='/download' element={<DownloadApp />} />
          <Route path='/update-required' element={<UpdateRequired />} />
          <Route path='/login' element={<MobileLogin />} />
          <Route path='/checkout' element={<AppLayout />}>
            <Route path='/checkout/:tokenName' element={<Checkout />} />
          </Route>
          <Route path='/buy' element={<AppLayout />}>
            <Route path='/buy/:tokenAddress' element={<Buy />} />
          </Route>
          <Route path='/stripe-checkout' element={<AppLayout />}>
            <Route
              path='/stripe-checkout/:tokenName'
              element={<StripeCheckout />}
            />
          </Route>
          {Capacitor.isNativePlatform() ? (
            authenticated ? (
              <Route path='/' element={<AppLayout />}>
                <Route path='/' element={<Dashboard />} />
              </Route>
            ) : (
              <Route path='/' element={<MobileLogin />} />
            )
          ) : (
            <Route path='/' element={<AppLayout />}>
              <Route path='/' element={<LandingV2 />} />
            </Route>
          )}
          <Route path='/promo-gift' element={<AppLayout />}>
            <Route path='/promo-gift/:id' element={<GiftRedirect />} />
          </Route>
          <Route path='/rewards' element={<AppLayout />}>
            <Route path='/rewards' element={<RewardsView />} />
          </Route>
          <Route path='/pricing' element={<AppLayout />}>
            <Route path='/pricing' element={<Pricing />} />
          </Route>
          <Route path='/partners' element={<AppLayout />}>
            <Route path='/partners' element={<Partners />} />
          </Route>
          {currentUser?.role === 'ADMIN' && (
            <Route path='/admin' element={<AppLayout />}>
              <Route path='/admin' element={<Admin />} />
            </Route>
          )}
          <Route path='/gift-cards' element={<AppLayout />}>
            <Route path='/gift-cards' element={<Physical />} />
          </Route>
          <Route path='/referrals' element={<Referrals />} />
          <Route path='/bridge-tos-callback' element={<BridgeCallback />} />
          <Route path='/bridge-kyc-complete' element={<BridgeKycCallback />} />
          <Route path='/gifts'>
            <Route path=':id'>
              <Route path='claim' element={<CheckEmail />} />
              <Route path='survey-claim' element={<CheckEmail />} />
            </Route>
          </Route>
          <Route
            path='/submission'
            element={<RedirectComponent to='/app/token-submission' />}
          />
          <Route
            path='/halloween'
            element={
              <RedirectComponent to='/app/tokens/XsbEhLAtcf6HdfpFZ5xEMdqW8nfAvcsP5bdudRLJzJp?showGift=true' />
            }
          />
          <Route path='/terms' element={<TermsOfUse />} />
          <Route path='/privacy' element={<PrivacyPolicy />} />
          <Route
            path='survey'
            element={
              <LandingContainer>
                <Navbar />
                <SurveyPromo />
                <Footer />
              </LandingContainer>
            }
          />
          <Route path='/app' element={<AppLayout />}>
            <Route
              path='discount/:discountCode'
              element={<DiscountCodeRoute />}
            />
            <Route path='home' element={<Dashboard />} />
            <Route path='home/deposit' element={<OpenDepositModalRoute />} />
            <Route path='cards' element={<Cards />} />
            <Route path='discover' element={<Home />} />
            <Route path='portfolio' element={<Dashboard />} />
            <Route path='token-submission' element={<TokenSubmission />} />
            <Route
              path='token-submission/:id/:action'
              element={<TokenSubmissionResponse />}
            />
            {enableSpend && (
              <>
                <Route path='spend' element={<SpendGallery />} />
                <Route path='spend/my-cards' element={<MyGiftCards />} />
                <Route
                  path='spend/category/:categoryName'
                  element={<SpendCategory />}
                />
                <Route path='spend/:id' element={<SpendGiftCardDetail />} />
                <Route path='spend/:id/checkout' element={<SpendCheckout />} />
              </>
            )}
            <Route path='gifts' element={<Gifts />}>
              <Route index element={<GiftsList />} />
              <Route path='create' element={<CreateGiftFlow />} />
              <Route path=':id'>
                <Route path='promo-claim' element={<ClaimPromoCodeGift />} />
                <Route element={<GiftDetails currentUser={currentUser} />}>
                  <Route index element={<GiftClaimRouter />} />
                  <Route path='claim' element={<GiftClaimRouter />} />
                  <Route path='status' element={<Status />} />
                </Route>
              </Route>
            </Route>
            <Route path='tokens/:address' element={<TokenHome />} />
            <Route path='trade' element={<Trade />} />
          </Route>
        </Routes>
        <MobileNavigation />
        <Modals />
        <NativeSelect />
        <Modal />
      </ModalProvider>
      {!(!currentUser && authenticated) && (
        <>
          <IonMenu
            contentId='main-content'
            swipeGesture={false}
            style={{
              '--background': currentTheme.background.primary,
            }}
          >
            <IonContent
              className='ion-padding'
              style={{
                '--background': currentTheme.background.primary,
              }}
            >
              <DropdownContent />
            </IonContent>
          </IonMenu>
          <IonMenu
            style={{
              '--width': '100vw',
              '--background': currentTheme.background.primary,
            }}
            menuId='notifications'
            contentId='main-content'
            side='end'
            swipeGesture={false}
          >
            <IonHeader>
              <IonToolbar
                style={{
                  '--min-height': Capacitor.isNativePlatform()
                    ? '66px'
                    : '76px',
                  '--background': currentTheme.background.primary,
                }}
              >
                <IonTitle
                  style={{
                    color: currentTheme.text.title,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 34,
                      width: '100%',
                      color: currentTheme.text.title,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 34,
                        width: '100%',
                        color: currentTheme.text.title,
                      }}
                    >
                      Notifications
                    </div>
                  </div>
                </IonTitle>
                <IonButtons slot='end'>
                  <IonButton
                    style={{
                      '--color': currentTheme.text.title,
                    }}
                    aria-label='Close menu'
                    onClick={() => {
                      hapticsImpactLight();
                      menuController.toggle('notifications');
                    }}
                  >
                    <IonIcon icon={closeOutline} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent
              className='ion-padding'
              style={{
                '--background': currentTheme.background.primary,
              }}
            >
              <NotificationsContent />
            </IonContent>
          </IonMenu>
        </>
      )}
      <ToastContainer
        theme={themeMode}
        style={{ zIndex: 99999 }}
        toastStyle={{ zIndex: 99999 }}
      />
    </Container>
  );
}

const lightModeConfig = {
  theme: 'light',
  logo: '/logo-footer.webp',
};

const darkModeConfig = {
  theme: 'dark',
  logo: '/logo-footer-dark.webp',
};

const ThemedApp = () => {
  const themeMode = useSelector(selectThemeMode);

  useEffect(() => {
    document.body.style.backgroundColor =
      theme.colors[themeMode].background.primary;
    document.body.style.color = theme.colors[themeMode].text.regular;
  }, [themeMode]);

  return (
    <ThemeProvider theme={theme.colors[themeMode]}>
      <WalletProvider
        appearance={themeMode === 'light' ? lightModeConfig : darkModeConfig}
      >
        <ReactApp />
      </WalletProvider>
    </ThemeProvider>
  );
};

export default ThemedApp;
